import { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';

/**
 * pollJobUntilDone — promise-based poll of the CD-3 async-job helper for
 * imperative flows (e.g. a Decode button that must await the job then reload).
 * Resolves with the final job doc ({status:'ready'|'error'|'stalled', result, error}).
 */
export async function pollJobUntilDone(apiBase, jobId, { interval = 2500, timeoutMs = 180000 } = {}) {
  const start = Date.now();
  // eslint-disable-next-line no-constant-condition
  while (true) {
    let j = null;
    try { ({ data: j } = await axios.get(`${apiBase}/jobs/${jobId}`)); }
    catch (_) { /* transient — keep polling */ }
    if (j && j.status && j.status !== 'generating') return j;
    if (Date.now() - start > timeoutMs) return { status: 'stalled', error: 'Decode timed out — retry.' };
    await new Promise((r) => setTimeout(r, interval));
  }
}


/**
 * useAsyncJob — uniform client for the CD-3 async-job helper.
 *
 * Any endpoint that returns `{ job_id, status }` (from services.async_jobs.enqueue)
 * can be driven through this hook. It polls GET /api/jobs/{id} and exposes a
 * single uniform state machine: idle → generating → ready | error | stalled.
 * Retry re-fires the original trigger (a fresh job, since the old one is no
 * longer in-flight). Endpoints may also return a direct result (e.g. a cached
 * hit with no `job_id`) — that resolves immediately as `ready`.
 *
 * @param {string} apiBase  e.g. `${REACT_APP_BACKEND_URL}/api`
 */
export function useAsyncJob(apiBase) {
  const [status, setStatus] = useState('idle');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);
  const lastTrigger = useRef(null);

  const stop = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  useEffect(() => () => stop(), [stop]);

  const poll = useCallback((jobId) => {
    stop();
    pollRef.current = setInterval(async () => {
      try {
        const { data: j } = await axios.get(`${apiBase}/jobs/${jobId}`);
        if (j.status === 'generating') return;   // keep polling
        stop();
        setStatus(j.status);
        setResult(j.result || null);
        setError(j.error || (j.status === 'stalled'
          ? 'The worker stopped responding (it may have crashed or restarted). Retry.'
          : null));
      } catch (_) { /* transient network — keep polling */ }
    }, 3000);
  }, [apiBase, stop]);

  const run = useCallback(async (triggerFn) => {
    lastTrigger.current = triggerFn;
    setStatus('generating'); setError(null); setResult(null);
    try {
      const res = await triggerFn();
      const data = res?.data || res;
      const jobId = data?.job_id || data?.id;
      if (!jobId) {                       // direct/cached result — no job
        setStatus('ready'); setResult(data); return data;
      }
      if (data.status && data.status !== 'generating') {
        setStatus(data.status); setResult(data.result || null); setError(data.error || null);
        return data;
      }
      poll(jobId);
      return data;
    } catch (e) {
      setStatus('error');
      setError(e.response?.data?.detail || e.message || 'Failed to start');
    }
  }, [poll]);

  const retry = useCallback(() => {
    if (lastTrigger.current) run(lastTrigger.current);
  }, [run]);

  const reset = useCallback(() => {
    stop(); setStatus('idle'); setResult(null); setError(null);
  }, [stop]);

  return {
    status, result, error, run, retry, reset,
    busy: status === 'generating',
    failed: status === 'error' || status === 'stalled',
  };
}
